/* eslint-disable @typescript-eslint/no-explicit-any */
import { MotifFormField, MotifErrorMessage } from "@ey-xd/motif-react";

import { useEffect, useId, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import SearchInput from "../SearchInput/SearchInput";
import { AppDispatch, RootState } from "../../../store";
import "./SingleSelectUserSearch.scss";
import {
  resetSearchUsers,
  searchUsers,
} from "../../../store/slices/CTDBUserListSlice";
import { MotifIcon } from "@ey-xd/motif-react";
import { MotifNavigationIcClose24px } from "@ey-xd/motif-icon";

interface ProxyOwner {
  Name: string;
  Email: string;
}

const SingleSelectUserSearch = ({
  proxyOwners,
  name = "",
  setProductContacts,
  validationMessage = "",
  onSearchChange,
}: any) => {
  const [userSearch, setUserSearch] = useState<string[]>([]);
  const [userCurrentSearch, setUserCurrentSearch] = useState<string>("");
  const [searchCharCount, setSearchCharCount] = useState(0);
  const [proxyOwnersList, setProxyOwnersList] =
    useState<ProxyOwner[]>(proxyOwners);
  const [showProxyOwnerSearch, setShowProxyOwnerSearch] = useState(
    proxyOwners && proxyOwners.length > 0 ? false : true,
  );
  const [expandProxyOwnerSearch, setExpandProxyOwnerSearch] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const searchAbortRef = useRef<{ abort: () => void } | null>(null);

  useEffect(() => {
    return () => {
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      dispatch(resetSearchUsers());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setProxyOwnersList(proxyOwners);
    setProductContacts(proxyOwners);
    setShowProxyOwnerSearch(
      proxyOwners && proxyOwners.length > 0 ? false : true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyOwners]);

  const userList = useSelector(
    (state: RootState) => state.CTDBUserListSlice.data,
  );
  const userListLoading = useSelector(
    (state: RootState) => state.CTDBUserListSlice.isLoading,
  );

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const inputValue = event.target.value;
    setSearchCharCount(inputValue?.length);
    setUserCurrentSearch(inputValue);
    onSearchChange?.(inputValue);
    if (inputValue.trim().length >= 3) {
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
      dispatch(resetSearchUsers());
      searchAbortRef.current = dispatch(
        searchUsers({ searchText: inputValue.trim() }),
      ) as unknown as { abort: () => void };
    }
  };

  const handleSearchClear = () => {
    dispatch(resetSearchUsers());
    setUserCurrentSearch("");
    setSearchCharCount(0);
    onSearchChange?.("");
  };

  const handleUserClick = (userData: string) => {
    if (!userData) return;
    const newProxyOwner = {
      Name: userData.split("(")[0].trim(),
      Email: userData.split("(")[1].split(")")[0].trim(),
    };
    setProxyOwnersList([newProxyOwner]);
    setProductContacts([newProxyOwner]);
    setUserSearch([...userSearch, userCurrentSearch]);
    setUserCurrentSearch("");
    dispatch(resetSearchUsers());
    setShowProxyOwnerSearch(false);
  };

  const handleDeleteClick = () => {
    dispatch(resetSearchUsers());
    setProxyOwnersList([]);
    setProductContacts([]);
    setUserSearch([]);
    setUserCurrentSearch("");
    setSearchCharCount(0);
    onSearchChange?.("");
    setExpandProxyOwnerSearch(false);
    setShowProxyOwnerSearch(true);
  };

  const handleSelectedUserGridClick = () => {
    setShowProxyOwnerSearch(
      proxyOwnersList && proxyOwnersList.length > 0
        ? false
        : !showProxyOwnerSearch,
    );
  };

  const filteredItems = Array.isArray(userList)
    ? userList.filter((userData: any) => {
        const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
        return emailRegex.test(userData.email);
      })
    : [];

  const displayItems = filteredItems.map(
    (userData: any) => `${userData.displayName} (${userData.email})`,
  );

  return (
    <>
      <div
        className={`single-selected-user-grid-${name} ${
          validationMessage ? "has-error" : ""
        }`}
        aria-invalid={Boolean(validationMessage)}
      >
        {proxyOwnersList && proxyOwnersList.length > 0 && (
          <div
            className={`selected-users`}
            onClick={handleSelectedUserGridClick}
          >
            {proxyOwnersList.map((user, index) => {
              if (index > 1) {
                return (
                  <div
                    key={index}
                    className={`selected-user ${
                      expandProxyOwnerSearch ? "" : "hide"
                    }`}
                  >
                    {user.Name}
                    <span className="remove-user" onClick={handleDeleteClick}>
                      <MotifIcon iconFunction={MotifNavigationIcClose24px} />
                    </span>
                  </div>
                );
              }
              return (
                <div key={index} className="selected-user">
                  {user.Name}
                  <span className="remove-user" onClick={handleDeleteClick}>
                    <MotifIcon iconFunction={MotifNavigationIcClose24px} />
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <MotifFormField>
        <div className={!showProxyOwnerSearch ? "hide" : ""}>
          <SearchInput
            aria-label="Search for a user"
            id={`user-search-${useId()}`}
            placeholder="Type a username or email"
            open={!userListLoading}
            className={`commercial-proxy-owner-search-input-${name} ${
              searchCharCount >= 3 ? "show-suggestions" : ""
            }`}
            items={
              userList &&
              userList
                .filter((userData: any) => {
                  const emailRegex =
                    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
                  return emailRegex.test(userData.email);
                })
                .map((userData: any) => {
                  return userData.displayName + " (" + userData.email + ")";
                })
            }
            onChange={(e) => handleSearchInputChange(e)}
            onSelect={(item: any) => handleUserClick(item)}
            visibleOptions={3}
            value={userCurrentSearch}
            name={name}
            onClear={handleSearchClear}
          />
          {userCurrentSearch.trim().length > 3 &&
            !userListLoading &&
            userList &&
            !displayItems.length &&
            !validationMessage && (
              <MotifErrorMessage
                aria-live="assertive"
                role="alert"
                id="aria-error-primaryList"
              >
                Please enter valid user name or email.
              </MotifErrorMessage>
            )}
        </div>
        {validationMessage && (
          <MotifErrorMessage aria-live="assertive" role="alert">
            {validationMessage}
          </MotifErrorMessage>
        )}
      </MotifFormField>
    </>
  );
};

export default SingleSelectUserSearch;
